import {
    CartOrderPaymentStatus, PaymentService
} from '../../shared/constants'
import { getCsobPaymentApiService } from '@simploshop-services/CsobPayment.service'
import { joinURL } from 'ufo'

export function useCart() {
    const store = useCartStore()
    return {
        ...store,
        ...storeToRefs(store),
    }
}

interface UseCartPaymentSummaryReturn {
    order: ComputedRef<InstanceType<typeof OrderModel> | null>
    paymentStatus: ComputedRef<CartOrderPaymentStatus>
}

/**
 * A composable to handle the cart payment summary.
 */
export function useCartPaymentSummary(): UseCartPaymentSummaryReturn {
    const route = useRoute()
    const cartStore = useCartStore()

    const order = computed(() =>
        // If the latest order is a dummy one (unknown status), do not return an order
        cartStore.latestOrder?.id === -1
            ? null
            : cartStore.latestOrder ?? null
    )

    const paymentStatus = computed<CartOrderPaymentStatus>(() => {
        if (cartStore.latestOrder?.id === -1) return CartOrderPaymentStatus.UNKNOWN
        if (order.value?.isPaid() === false && !order.value?.isToBePaidLater()) return CartOrderPaymentStatus.ERROR

        return CartOrderPaymentStatus.SUCCESS
    })

    // throw an error if the order is missing and it isn't a dummy order
    if (order.value === null && cartStore.latestOrder?.id !== -1) {
        console.error(`[useCartPaymentSummary]: Order is \`null\` for route \`${route.fullPath}\`. This should never happen.`)
        throw createError({
            statusCode: 404,
            fatal: true,
        })
    }

    return {
        order: order as UseCartPaymentSummaryReturn['order'],
        paymentStatus: paymentStatus,
    }
}


export function useCartPayment() {
    const paymentReturnUrlBase = useFullUrl('/api/payment-return')
    const goPayClient = useClientGoPay()

    async function createGoPayPayment(orderId: number | null | undefined) {
        if (!orderId) throw new Error('Order ID is missing.')

        const goPayResponsePromise = getGoPayApiService().post({
            order_id: orderId,
            payload: null,
            return_url: joinURL(paymentReturnUrlBase, PaymentService.GoPay),
        })

        await goPayClient.checkout(
            goPayResponsePromise.then(r => r.getItem()?.gateUrl ?? null)
        )

        await new Promise(() => {})
    }

    async function createCsobPayment(orderId: number | null | undefined) {
        if (!orderId) throw new Error('Order ID is missing.')

        const csobResponse = await getCsobPaymentApiService().post({
            order_id: orderId,
            return_uri: joinURL(paymentReturnUrlBase, PaymentService.CSOB),
            return_method: 'POST',
        })

        const gateUrl = csobResponse?.getItem()?.gatewayUri ?? null
        if (!gateUrl) {
            // TODO: throw a custom error with translated message
            throw new Error('CSOB payment gateway URL is missing.')
        }

        await navigateTo(gateUrl, {
            external: true,
        })

        await new Promise(() => {})
    }

    return {
        createGoPayPayment,
        createCsobPayment,
    }
}
